import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme';
import GlobalFontStyles from './components/GlobalFontStyles';
import { JuryAuthProvider } from './contexts/JuryAuthContext';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store/store';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Fade,
  Backdrop,
  TextField
} from '@mui/material';
import { Link } from 'react-router-dom';
import Dashboard from './pages/Dashboard';

// Import layouts
import AdminLayout from './components/layout/AdminLayout';
import ArtistLayout from './components/layout/ArtistLayout';
import GuestLayout from './components/layout/GuestLayout';
import DomainUserLayout from './components/layout/DomainUserLayout';
import PublicLayout from './components/layout/PublicLayout';

// Import route configurations
import { publicRoutes } from './routes/publicRoutes';
import { adminRoutes } from './routes/adminRoutes';
import { artistRoutes } from './routes/artistRoutes';
import { domainUserRoutes } from './routes/domainUserRoutes';
import { guestRoutes } from './routes/guestRoutes';


// Import components for public routes
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';

const queryClient = new QueryClient();


// Flash Notification Component - Center screen flash
const FlashNotification: React.FC<{ 
  open: boolean; 
  message: string; 
  onClose: () => void; 
}> = ({ open, message, onClose }) => {
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        onClose();
      }, 800); // Show for 0.8 seconds
      
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  return (
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      }}
      open={open}
    >
      <Fade in={open} timeout={100}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            px: 4,
            py: 2,
            borderRadius: 2,
            boxShadow: 8,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            minWidth: 200,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          {message}
        </Box>
      </Fade>
    </Backdrop>
  );
};

// Help Modal Component
const CommandPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  
  const commands = [
    { key: 'B', label: 'Bands', path: '/admin/bands' },
    { key: 'S', label: 'Strands', path: '/admin/strands' },
    { key: 'X', label: 'Programming', path: '/admin/programming' },
    { key: 'F', label: 'Finance', path: '/admin/budget-overview' },
    { key: 'V', label: 'Venues', path: '/admin/venues' },
    { key: 'T', label: 'Ticket Sales', path: '/admin/sales' },
    { key: 'A', label: 'Applications', path: '/admin/applications' },
    { key: 'U', label: 'Users', path: '/admin/users' },
    { key: 'Y', label: 'Personnel', path: '/admin/personnel' },
    { key: 'I', label: 'Inventory', path: '/admin/inventory' },
    { key: 'P', label: 'Performances', path: '/admin/performances' },
    { key: 'R', label: 'Rota', path: '/admin/rota' },
    { key: 'E', label: 'Expenses', path: '/admin/expenses/new' },
    { key: 'D', label: 'Dashboard', path: '/admin' },
    { key: 'W', label: 'Invoices', path: '/admin/invoices/new' },
  ];
  
  const filtered = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(search.toLowerCase())
  );
  
  const handleSelect = (path: string) => {
    navigate(path);
    onClose();
    setSearch('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filtered.length > 0) {
      handleSelect(filtered[0].path);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search commands..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ p: 2, pb: 0 }}
        />
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filtered.map((cmd) => (
            <Box
              key={cmd.key}
              onClick={() => handleSelect(cmd.path)}
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                display: 'flex',
                gap: 2,
                alignItems: 'center'
              }}
            >
              <Typography variant="body1">{cmd.label}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
// Updated KeyboardShortcutHandler component with comprehensive focus detection
const KeyboardShortcutHandler: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [commandPaletteOpen, setCommandPaletteOpen] = React.useState(false);
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const [flashNotification, setFlashNotification] = React.useState({
    open: false,
    message: ''
  });

  // Function to show flash notification
  const showFlashNotification = (message: string) => {
    setFlashNotification({ open: true, message });
  };

// Comprehensive detection for all interactive MUI components and navigation contexts
const isInteractiveElementFocused = () => {
  const activeElement = document.activeElement as HTMLElement;
  
  if (!activeElement) return false;
  
  // ====== BASIC INPUT ELEMENTS ======
  const inputTags = ['INPUT', 'TEXTAREA', 'SELECT'];
  if (inputTags.includes(activeElement.tagName)) return true;
  
  // ====== CONTENTEDITABLE ======
  if (activeElement.contentEditable === 'true') return true;
  if (activeElement.getAttribute('contenteditable') === 'true') return true;
  
  // ====== ARIA ROLES - COMPREHENSIVE LIST ======
  const interactiveRoles = [
    // Input-like roles
    'textbox', 'combobox', 'searchbox', 'spinbutton', 'slider',
    // Selection roles
    'listbox', 'option', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
    // Navigation roles
    'tab', 'tabpanel', 'tree', 'treeitem', 'grid', 'gridcell', 'row',
    // Button-like roles
    'button', 'switch', 'checkbox', 'radio',
    // Dialog/Modal roles
    'dialog', 'alertdialog', 'menu', 'menubar'
  ];
  
  const elementRole = activeElement.getAttribute('role');
  if (elementRole && interactiveRoles.includes(elementRole)) return true;
  
  // ====== MUI COMPONENT CLASSES - COMPREHENSIVE ======
  const muiInteractiveClasses = [
    // Input components
    'MuiInputBase-root', 'MuiInputBase-input', 'MuiOutlinedInput-root',
    'MuiFilledInput-root', 'MuiInput-root', 'MuiTextField-root',
    
    // Select/Autocomplete
    'MuiSelect-root', 'MuiSelect-select', 'MuiAutocomplete-root', 
    'MuiAutocomplete-input', 'MuiAutocomplete-inputRoot', 'MuiAutocomplete-option',
    'MuiAutocomplete-listbox', 'MuiAutocomplete-popper',
    
    // Date pickers (MUI X)
    'MuiDatePicker-root', 'MuiTimePicker-root', 'MuiDateTimePicker-root',
    'MuiPickersDay-root', 'MuiPickersCalendarHeader-root', 'MuiPickersYear-root',
    'MuiPickersMonth-root', 'MuiClock-root', 'MuiClockNumber-root',
    'MuiPickersPopper-root', 'MuiDesktopDatePicker-root',
    
    // Navigation components
    'MuiTabs-root', 'MuiTab-root', 'MuiTabPanel-root',
    'MuiAccordion-root', 'MuiAccordionSummary-root', 'MuiAccordionDetails-root',
    'MuiTreeView-root', 'MuiTreeItem-root',
    
    // Data display with interaction
    'MuiDataGrid-root', 'MuiDataGrid-cell', 'MuiDataGrid-row',
    'MuiTable-root', 'MuiTableRow-root', 'MuiTableCell-root',
    'MuiTableSortLabel-root', 'MuiTablePagination-root',
    
    // Buttons and controls
    'MuiButton-root', 'MuiIconButton-root', 'MuiToggleButton-root',
    'MuiSwitch-root', 'MuiCheckbox-root', 'MuiRadio-root', 'MuiSlider-root',
    'MuiChip-root', 'MuiChip-deleteIcon',
    
    // Menus and dropdowns
    'MuiMenu-root', 'MuiMenuItem-root', 'MuiMenuList-root',
    'MuiPopover-root', 'MuiPopper-root', 'MuiPaper-root',
    
    // Modal/Dialog contexts
    'MuiDialog-root', 'MuiModal-root', 'MuiDrawer-root',
    'MuiSnackbar-root', 'MuiAlert-root'
  ];
  
  // Check element classes
  const elementClasses = Array.from(activeElement.classList);
  if (muiInteractiveClasses.some(cls => elementClasses.includes(cls))) return true;
  
  // ====== INTERACTIVE STATES ======
  const interactiveStates = [
    '[tabindex]', // Any tabbable element
    '[aria-haspopup="true"]', // Has popup/dropdown
    '[aria-expanded="true"]', // Currently expanded
    '.Mui-focused', // MUI focused state
    '.MuiInputBase-focused', // Input focused
    '.Mui-selected', // Selected item
    '.Mui-disabled', // Even disabled elements shouldn't trigger shortcuts
    '[contenteditable]', // Any contenteditable
    '[draggable="true"]' // Draggable elements
  ];
  
  if (interactiveStates.some(selector => 
    activeElement.matches(selector) || activeElement.closest(selector)
  )) return true;
  
  // ====== DROPDOWN/POPUP CONTEXTS ======
  // Check if any dropdown/popup is open anywhere on the page
  const openInteractiveElements = document.querySelectorAll([
    '[aria-expanded="true"]',
    '.MuiSelect-select[aria-expanded="true"]',
    '.MuiAutocomplete-popper',
    '.MuiPopover-root', 
    '.MuiMenu-root',
    '.MuiPickersPopper-root', // Date picker popup
    '.MuiAutocomplete-listbox',
    '.MuiDialog-root', // Any open dialog
    '.MuiModal-root', // Any open modal
    '.MuiDrawer-root[aria-hidden="false"]', // Open drawer
    '.MuiSnackbar-root' // Active snackbar
  ].join(', '));
  
  if (openInteractiveElements.length > 0) return true;
  
  // ====== PARENT TREE TRAVERSAL ======
  let parent = activeElement.parentElement;
  let depth = 0;
  const maxDepth = 6; // Slightly deeper for complex MUI structures
  
  while (parent && depth < maxDepth) {
    const parentClasses = Array.from(parent.classList);
    
    // Check parent for interactive classes
    if (muiInteractiveClasses.some(cls => parentClasses.includes(cls))) return true;
    
    // Check parent roles
    const parentRole = parent.getAttribute('role');
    if (parentRole && interactiveRoles.includes(parentRole)) return true;
    
    // Check for interactive containers
    if (interactiveStates.some(selector => parent?.matches(selector))) return true;
    
    // Special checks for MUI portals and complex structures
    if (parentClasses.some(cls => [
      'MuiPopper-root', 'MuiModal-root', 'MuiPopover-root',
      'MuiDialog-root', 'MuiDrawer-root', 'MuiBackdrop-root',
      'MuiPickersPopper-root', 'MuiAutocomplete-popper'
    ].includes(cls))) return true;
    
    parent = parent.parentElement;
    depth++;
  }
  
  // ====== SPECIAL NAVIGATION CONTEXTS ======
  // Check if we're in a keyboard navigation context
  
  // Active tab navigation
  const activeTabs = document.querySelectorAll('.MuiTabs-root .Mui-selected, .MuiTab-root:focus');
  if (activeTabs.length > 0) return true;
  
  // Active accordion
  const activeAccordions = document.querySelectorAll('.MuiAccordionSummary-root:focus, .MuiAccordion-root .Mui-expanded');
  if (activeAccordions.length > 0) return true;
  
  // Data grid/table with active cell
  const activeTableCells = document.querySelectorAll([
    '.MuiDataGrid-cell:focus',
    '.MuiDataGrid-cell.Mui-selected',
    '.MuiTableCell-root:focus',
    '.MuiTableRow-root.Mui-selected'
  ].join(', '));
  if (activeTableCells.length > 0) return true;
  
  // Tree view navigation
  const activeTreeItems = document.querySelectorAll('.MuiTreeItem-root:focus, .MuiTreeItem-root.Mui-selected');
  if (activeTreeItems.length > 0) return true;
  
  // ====== KEYBOARD INTERACTION INDICATORS ======
  // Check for elements that are currently receiving keyboard interaction
  
  // Elements with recent keyboard focus (within last few seconds)
  if (activeElement.classList.contains('focus-visible') || 
      activeElement.matches(':focus-visible')) return true;
  
  // Elements that are programmatically focused
  if (document.querySelector('[data-focus-visible-added]')) return true;
  
  // ====== FORM NAVIGATION CONTEXT ======
  // If we're anywhere inside a form that has interactive elements
  const parentForm = activeElement.closest('form');
  if (parentForm) {
    const formInteractiveElements = parentForm.querySelectorAll([
      'input', 'textarea', 'select', 'button',
      '[role="button"]', '[role="combobox"]', '[role="slider"]',
      '.MuiInputBase-root', '.MuiSelect-root', '.MuiButton-root',
      '.MuiCheckbox-root', '.MuiRadio-root', '.MuiSwitch-root'
    ].join(', '));
    
    // If form has many interactive elements and we're focused somewhere in it
    if (formInteractiveElements.length > 0 && 
        (activeElement.matches('[tabindex]') || activeElement.closest('[tabindex]'))) {
      return true;
    }
  }
  
  return false;
};

  useEffect(() => {
   const handleKeyDown = (event: KeyboardEvent) => {
  if (!user) return;
  
  // Cmd/Ctrl + K opens command palette
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    setCommandPaletteOpen(true);
  }
  
  // ESC closes command palette
  if (event.key === 'Escape' && commandPaletteOpen) {
    setCommandPaletteOpen(false);
  }
};
    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, user]);

  return (
  <>
    <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
    <FlashNotification 
      open={flashNotification.open} 
      message={flashNotification.message}
      onClose={() => setFlashNotification({ open: false, message: '' })}
    />
  </>
);
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <JuryAuthProvider>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <CssBaseline />
                  <GlobalFontStyles />
                  <Router>
                    <KeyboardShortcutHandler />
                    <AppRoutes />
                    <ToastContainer
                      position="top-right"
                      autoClose={330}
                      hideProgressBar={false}
                      newestOnTop
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                    />
                  </Router>
                </LocalizationProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </AuthProvider>
        </JuryAuthProvider>
      </PersistGate>
    </Provider>
  );
};

const AppRoutes: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for session expired message
    const sessionExpired = searchParams.get('sessionExpired');
    if (sessionExpired === 'true') {
      toast.info('Your session expired. Please log in again.');
    }
  }, [searchParams]);

  // Debug logging for troubleshooting
  useEffect(() => {
    if (user) {
      console.log('AppRoutes: User authenticated with role:', user.role);
    }
  }, [user]);

  // Show loading state
  if (loading) {
    console.log('AppRoutes: Still loading authentication state');
    return <div>Loading...</div>; 
  }

  if (isAuthenticated && user) {
    console.log("AppRoutes: User authenticated with role:", user.role);
    
    switch (user.role) {
      case 'superadmin':
      case 'admin':
      case 'gm':
        return (
          <Routes>
            <Route element={<AdminLayout />}>
              {adminRoutes?.[0]?.children?.map((route, index) => (
                <Route key={index} path={route.path} element={route.element} />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        );
      case 'artist':
        return (
          <Routes>
            <Route element={<ArtistLayout />}>
              {artistRoutes?.[0]?.children?.map((route, index) => (
                <Route key={index} path={route.path} element={route.element} />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/artist/dashboard" replace />} />
          </Routes>
        );
      case 'team':
        return (
          <Routes>
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<Dashboard />} />
              {adminRoutes?.[0]?.children?.map((route, index) => (
                route.path ? (
                  <Route key={index} path={route.path} element={route.element} />
                ) : null
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        );
      case 'domainUser':
        return (
          <Routes>
            <Route element={<DomainUserLayout />}>
              {domainUserRoutes?.[0]?.children?.map((route, index) => (
                <Route key={index} path={route.path} element={route.element} />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/domain" replace />} />
          </Routes>
        );
      case 'guest':
        return (
          <Routes>
            <Route element={<GuestLayout />}>
              {guestRoutes?.[0]?.children?.map((route, index) => (
                <Route key={index} path={route.path} element={route.element} />
              ))}
            </Route>
            <Route path="*" element={<Navigate to="/guest" replace />} />
          </Routes>
        );
      default:
        console.error('AppRoutes: Unknown user role:', user.role);
        return <Navigate to="/login" replace />;
    }
  }

  // User not authenticated
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        {publicRoutes?.[0]?.children?.map((route, index) => (
          <Route key={index} path={route.path} element={route.element} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/bands" replace />} />
    </Routes>
  );
};

export default App;